/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useRef } from 'react';
import { compact } from 'lodash';
import { message } from 'antd';
import { getUsersApi } from 'redux/api/userApi';
import { getTranslated } from './index';

export const useForm = (callback, validate, allValues, Suffix, props = {}) => {
    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (Object.keys(errors).length === 0 && isSubmitting) {
            callback({ ...allValues, ...values });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errors]);

    const reset = () => {
        setValues({});
        setErrors({});
        setIsSubmitting(false);
    };

    const handleSubmit = (event) => {
        if (event) event.preventDefault();

        setErrors(validate({ ...allValues, ...values }, Suffix, props));

        setIsSubmitting(true);
    };

    const handleChange = (name, value) => {
        setValues(oldValues => ({ ...oldValues, [name]: value }));

        if (errors[name]) {
            const modifiedErrors = errors;

            modifiedErrors[name] = null;
            setErrors(modifiedErrors);
        }
    };

    return {
        handleChange,
        handleSubmit,
        reset,
        values,
        errors,
    };
};


/**
 * Debounce function is essential to ensuring a given task doesn't fire so often that it bricks browser performance.
 * It limits the rate at which a function can fire.
 * This is hook of debounce function
 * @tutorial https://dev.to/gabe_ragland/debouncing-with-react-hooks-jci
 * @param {string} value The string value of search
 * @param {number} delay Delay in milleseconds
 * @author Youssef Lahssini
 */
export const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [delay, value]);

    return debouncedValue;
};


export const useInterval = (callback, delay) => {
    const savedCallback = useRef();

    useEffect(() => {
        savedCallback.current = callback;
    });

    useEffect(() => {
        function tick() {
            savedCallback.current();
        }

        const id = setInterval(tick, delay);
        return () => clearInterval(id);
    }, [delay]);
};


export const useNavigatorOnLine = (locale) => {
    function handleOnline() {
        message.destroy();
    }

    function handleOffline() {
        message.warning({
            content: getTranslated('global.noInternet', locale),
            duration: 0,
            // eslint-disable-next-line react/jsx-filename-extension
            icon: <i className="la la-wifi la-lg gx-mr-2" style={{ color: 'orange' }} />,
        });
    }

    useEffect(() => {
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOffline);
            window.removeEventListener('offline', handleOffline);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};


export const useCounter = (number, duration) => {
    const [count, setCount] = useState('0');

    useEffect(() => {
        let start = 0;
        const end = number.toString().substring(3);
        const endInt = parseInt(number, 10);

        if (start === endInt) {
            return;
        }

        const incrementing = duration / end;

        const timer = setInterval(() => {
            start += 1;

            setCount(String(start) + number.toString().substring(3));

            if (start === endInt) {
                clearInterval(timer);
            }
        }, incrementing);
    }, [number, duration]);

    return count;
};


export const useSalesAgent = (id) => {
    const [salesAgents, setSalesAgents] = useState({ loading: false, data: [] });

    useEffect(() => {
        async function getSaleAgents() {
            const userGroups = compact([process.env.REACT_APP_SALES_GROUP, process.env.REACT_APP_CS_GROUP]);

            setSalesAgents(state => ({ ...state, loading: true }));

            const resultUsers = await getUsersApi({
                filter: JSON.stringify({
                    user_group_id: userGroups,
                    status: true,
                }),
                limit: 999,
            });

            if (resultUsers.status === 200) {
                let registeredUser = [];
                if (id && !resultUsers.data.hits.map(u => u._id).includes(id)) {
                    const resultUser = await getUsersApi({
                        filter: JSON.stringify({ _id: id }),
                        limit: 1,
                    });

                    registeredUser = resultUser.data.hits;
                }

                setSalesAgents(state => ({ ...state, loading: false, data: [...resultUsers.data.hits, ...registeredUser] }));
            } else {
                setSalesAgents(state => ({ ...state, loading: false, data: [] }));
            }
        }

        getSaleAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return salesAgents;
};

export const usePrevious = (value) => {
    const ref = useRef();

    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current;
};
